// src/components/ServiceGrid.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Props:
 *  - level: 'xa'|'huyen'|'tinh'  (defaults to 'xa')
 *  - filters: { q, province, district, ward }
 *  - onCounts: function({xa,huyen,tinh}) optional
 *
 * ServiceGrid sẽ fetch "/api/services" ONCE, normalize data, compute counts,
 * then filter client-side according to props.level and props.filters.
 */
export default function ServiceGrid({ level = "xa", filters = {}, onCounts }) {
  const navigate = useNavigate();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Fetch all services once
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch("/api/services", { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const raw = Array.isArray(data) ? data : data.items ?? [];

        const normalized = raw.map((s, idx) => {
          const lvlRaw = (s.level ?? s.cap ?? s.scope ?? "xa") + "";
          const lvl = lvlRaw.trim().toLowerCase();
          return {
            id: s.id ?? idx + 1,
            name: (s.name ?? s.title ?? "").toString(),
            slug: (s.slug ?? "").toString(),
            description: (s.description ?? "").toString(),
            level: lvl,
            province: (s.province ?? s.province_name ?? s.provinceId ?? s.province_id ?? "").toString(),
            district: (s.district ?? s.district_name ?? s.districtId ?? s.district_id ?? "").toString(),
            ward: (s.ward ?? s.ward_name ?? s.wardId ?? s.ward_id ?? "").toString(),
            address: (s.address ?? s.location ?? "").toString(),
            idx: s.idx ?? s.order ?? s.sort_order ?? s.id ?? idx + 1,
            raw: s,
          };
        }).sort((a, b) => (a.idx ?? a.id) - (b.idx ?? b.id));

        if (!alive) return;
        setAll(normalized);
      } catch (e) {
        if (!alive) return;
        console.error(e);
        setError("Không tải được dữ liệu dịch vụ.");
        setAll([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // counts memo
  const counts = useMemo(() => {
    const c = { xa: 0, huyen: 0, tinh: 0 };
    for (const it of all) {
      if (it.level === "xa") c.xa++;
      else if (it.level === "huyen") c.huyen++;
      else if (it.level === "tinh") c.tinh++;
    }
    return c;
  }, [all]);

  useEffect(() => {
    if (typeof onCounts === "function") onCounts(counts);
  }, [counts, onCounts]);

  // helper: normalize & match
  const norm = (v = "") => (v + "").trim().toLowerCase();

  // filtered rows memo (fast)
  // ✅ Tạo danh sách hiển thị (đã fix missing vars)
  const rows = useMemo(() => {
    // 1) Tính các biến filter NGAY TRONG useMemo
    const q = norm(filters?.q ?? "");
    const prov = norm(filters?.province ?? "");
    const dist = norm(filters?.district ?? "");
    const ward = norm(filters?.ward ?? "");
    const lv = (level || "xa").toLowerCase();

    // 2) matches cục bộ để ESLint không bắt deps
    const matches = (hay = "", needle = "") => {
      if (!needle) return true;
      const h = (hay + "").toLowerCase();
      return h.includes(needle); // needle đã là lowercase ở trên
    };

    // 3) Lọc
    return all.filter((s) => {
      const nameOk =
        matches(s.name, q) ||
        matches((s.tags || []).join(" "), q);

      const provOk =
        !prov ||
        norm(s.province) === prov ||
        norm(s.provinceCode) === prov;

      const distOk =
        !dist ||
        norm(s.district) === dist ||
        norm(s.districtCode) === dist;

      const wardOk =
        !ward ||
        norm(s.ward) === ward ||
        norm(s.wardCode) === ward;

      const levelOk =
        !lv ||
        norm(s.level || s.addrLevel || "") === lv;

      return nameOk && provOk && distOk && wardOk && levelOk;
    });
  }, [all, level, filters]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-16 rounded-2xl bg-white ring-1 ring-slate-100 shadow-sm animate-pulse" />
        ))}
      </div>
    );
  }
  if (error) {
    return <div className="px-3 py-4 rounded-xl bg-rose-50 text-rose-700 ring-1 ring-rose-200">{error}</div>;
  }
  if (!rows.length) {
    return <div className="px-3 py-4 rounded-xl bg-white ring-1 ring-slate-100 text-slate-500">Không tìm thấy dịch vụ.</div>;
  }

  return (
    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-5">
      {rows.map((s, i) => (
        <button
          key={`${s.id}-${i}`}
          type="button"
          className="border rounded-2xl text-left p-4 bg-white shadow-sm hover:shadow-md active:opacity-80"
          onClick={() => navigate(`/services/${s.id}`)}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 text-center text-gray-500 tabular-nums text-sm font-medium leading-6">
              {i + 1}
            </div>
            <div className="min-w-0">
              <div className="text-base sm:text-lg font-semibold leading-6 truncate">{s.name}</div>
              <div className="text-gray-500 text-sm truncate">{s.province ? `${s.province}${s.district ? ` • ${s.district}` : ""}` : s.address}</div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}
